// Shared store-efficiency calculation. Mirrors the gauge component so the Today
// hero can compute the same climb without duplicating it.
//
// FOUR-RUNG rework (docs/06 §2.7, decision §2.8.1): the gauge is now a CLIMB TO
// YOUR OWN BEST WEEKS, not "% of industry potential".
//   realized   = Σ visitors × engagement × conversion × avg_value        (fill)
//   ownBest    = Σ visitors × max(engagement, Rung1_Engagement) × conv × avg
//                = the 100%-ANCHOR (your own best weeks, Rung 1 aggregate)
//   headlinePct= realized / ownBest × 100   → "78% of the way to your own best"
//   missedToOwnBest = ownBest − realized    → closeable just by matching them
//
// Beyond 100% we expose TICKS (not the anchor):
//   cohortTotal   = Σ v × cohort_p80_engagement × conv × avg  (Rung 3, optional)
//   ceilingTotal  = Σ v × Emax × Cmax × avg                   (Rung 4, research)
// with *TickPct = total / ownBest × 100 (> 100) so the gauge can draw peers and
// best-in-class beyond the own-best anchor, proving you can exceed your own best.
//
// Rung1_Engagement is per zone from zoneOverrides (nightly ETL). A zone without
// one contributes its current engagement (max() ⇒ no phantom headroom). The
// legacy keys realizedTotal / reachableTotal / missedRelativeTotal / efficiencyPct
// are kept (reachable == ceilingTotal) so existing consumers don't break while
// the gauge component migrates to the own-best anchor.

import { resolveZoneCeiling } from '@/prioritization/ceilingsRef'

export function computeStoreEfficiency(fixedWidgetsData, segmentKey = 'default', zoneOverrides = {}, cohort = null) {
  if (!fixedWidgetsData) {
    return {
      realizedTotal: 0,
      reachableTotal: 0,
      missedRelativeTotal: 0,
      efficiencyPct: 0,
      ownBestTotal: 0,
      ceilingTotal: 0,
      cohortTotal: null,
      missedToOwnBestTotal: 0,
      headlinePct: 0,
      peerTickPct: null,
      ceilingTickPct: null,
      notMeasurable: false,
    }
  }

  const funnels   = fixedWidgetsData.product_zone_funnels || {}
  const visitorsC = fixedWidgetsData.product_zone_visitors_counts || {}
  const clearC    = fixedWidgetsData.product_zone_clear_lingerers_counts || {}
  const cohortEng = cohort && Number.isFinite(cohort.engagement) ? cohort.engagement : null

  const zones = new Set([
    ...Object.keys(funnels),
    ...Object.keys(visitorsC),
    ...Object.keys(clearC)
  ])

  // platform_capabilities.md §17.7 Phase 2 — a tripwire (counts-only) zone can't measure engagement or
  // conversion, so the €-efficiency climb has no input. When EVERY product zone is
  // counts-only (a footfall store), return an explicit notMeasurable state instead
  // of a misleading 0% / max missed-€; the hero + gauge render a "not measurable"
  // card (SERVE/DEPLOY pattern), never a red 0%.
  const geometry = fixedWidgetsData.product_zones_geometry || {}
  const isMeasurable = (z) => (geometry[z] || 'polygon') !== 'tripwire'
  const zoneList = [...zones]
  const measurableZones = zoneList.filter(isMeasurable)
  if (zoneList.length && !measurableZones.length) {
    return {
      realizedTotal: 0,
      reachableTotal: 0,
      missedRelativeTotal: 0,
      efficiencyPct: 0,
      ownBestTotal: 0,
      ceilingTotal: 0,
      cohortTotal: null,
      missedToOwnBestTotal: 0,
      headlinePct: 0,
      peerTickPct: null,
      ceilingTickPct: null,
      notMeasurable: true,
    }
  }

  let realizedTotal = 0
  let reachableTotal = 0
  let ownBestTotal = 0
  let cohortTotal = 0

  zones.forEach(zone => {
    // Counts-only tripwire zones are engagement-unmeasurable → excluded from the
    // € climb entirely (their engagement 0 would otherwise inflate missed-€).
    if (!isMeasurable(zone)) return
    const v   = Number(visitorsC[zone] || 0)
    const clr = Number(clearC[zone] || 0)
    const avg = Number(funnels[zone]?.average_product_value || 0)
    const convRaw = Number(funnels[zone]?.conversion_rate || 0)

    const conversionRate = Math.min(
      1,
      Math.max(0, convRaw <= 1 ? convRaw : convRaw / 100)
    )
    const engagementRate = v > 0 ? Math.min(1, Math.max(0, clr / v)) : 0

    const ceilings = resolveZoneCeiling(segmentKey, zoneOverrides[zone], 'product')

    // Own-best engagement anchor: never below current, so ownBest ≥ realized
    // per zone (a zone already past its own best sits at its local 100%).
    const ov = zoneOverrides[zone]
    const r1 = ov && typeof ov === 'object' && Number.isFinite(ov.rung1Engagement) ? ov.rung1Engagement : null
    const ownBestEng = Math.max(engagementRate, (r1 != null && r1 > 0 && r1 <= 1) ? r1 : 0)

    realizedTotal  += v * engagementRate * conversionRate * avg
    reachableTotal += v * ceilings.Emax  * ceilings.Cmax  * avg
    ownBestTotal   += v * ownBestEng     * conversionRate * avg
    if (cohortEng != null) cohortTotal += v * cohortEng * conversionRate * avg
  })

  const ceilingTotal = reachableTotal
  const missedRelativeTotal = Math.max(0, reachableTotal - realizedTotal)
  const missedToOwnBestTotal = Math.max(0, ownBestTotal - realizedTotal)
  const efficiencyPct =
    reachableTotal > 0 ? (realizedTotal / reachableTotal) * 100 : 0
  const headlinePct =
    ownBestTotal > 0 ? (realizedTotal / ownBestTotal) * 100 : 0
  const cohortOut = cohortEng != null ? cohortTotal : null
  const peerTickPct = (cohortEng != null && ownBestTotal > 0) ? (cohortTotal / ownBestTotal) * 100 : null
  const ceilingTickPct = ownBestTotal > 0 ? (ceilingTotal / ownBestTotal) * 100 : null

  return {
    realizedTotal,
    reachableTotal,
    missedRelativeTotal,
    efficiencyPct,
    // four-rung climb
    ownBestTotal,
    ceilingTotal,
    cohortTotal: cohortOut,
    missedToOwnBestTotal,
    headlinePct,
    peerTickPct,
    ceilingTickPct,
    notMeasurable: false,
  }
}

/**
 * Descriptive "top weeks" context for the hero.
 *
 * PastPeakEngagement_p95 (written by the nightly pipeline as the
 * 95th-percentile-of-weekly engagement rate per zone) is NOT used
 * as a ceiling anywhere — the ceiling math always uses the industry
 * sector default. The P95 value is purely a self-comparison reference,
 * surfaced on the hero as "In your top weeks, zones engaged X% of
 * visitors at a zone average conversion of Y% — €Z of the €W above is
 * closeable just by matching these top weeks."
 *
 * Returns null when no zone has a past-peak value yet.
 *
 * Returns:
 *   {
 *     topWeeksEngagementPct: 38.2, // visitor-weighted mean across zones
 *                                  // of each zone's P95 weekly engagement
 *     zoneAvgConversionPct:  10.0, // visitor-weighted mean of current
 *                                  // admin-set conversion rates
 *     deltaEuro: 12450,            // store-wide € gain from matching P95
 *     atLevel: false,              // true when current ≥ P95 everywhere
 *   }
 *
 * Math per zone:
 *   delta = visitors × max(0, p95 − current_eng) × current_conv × avg
 *
 * Engagement comes from camera-measured visitor counts; conversion
 * comes from admin DetectionZoneSettings (no POS integration in the
 * default deployment) — so the conversion figure should be marked
 * "(est.)" in display copy. The € figure rolls both together and
 * is reported as-is.
 */
export function computeTopWeeksContext(fixedWidgetsData, zoneOverrides = {}) {
  const data = fixedWidgetsData || {}
  const funnels   = data.product_zone_funnels || {}
  const visitorsC = data.product_zone_visitors_counts || {}
  const clearC    = data.product_zone_clear_lingerers_counts || {}
  const overrides = zoneOverrides || {}

  const zoneNames = Object.keys(funnels)
  if (!zoneNames.length) return null

  let totalVisitorWeight = 0
  let weightedTopWeekEng = 0
  let weightedCurrentConv = 0
  let deltaEuro = 0
  let anyZoneHasP95 = false

  for (const zone of zoneNames) {
    const v = Number(visitorsC[zone] || 0)
    if (v <= 0) continue

    const overrideEntry = overrides[zone]
    // Prefer the four-rung Rung1_Engagement; fall back to the legacy
    // pastPeakEngagementP95 / selfCalibratedEmax aliases some surfaces
    // (demo data, older call sites) may still pass.
    const p95 = overrideEntry && typeof overrideEntry === 'object'
      ? Number(overrideEntry.rung1Engagement ?? overrideEntry.pastPeakEngagementP95 ?? overrideEntry.selfCalibratedEmax)
      : null
    if (!(Number.isFinite(p95) && p95 > 0 && p95 <= 1)) continue
    anyZoneHasP95 = true

    const clr = Number(clearC[zone] || 0)
    const curEng = v > 0 ? clr / v : 0
    const convRaw = Number(funnels[zone]?.conversion_rate || 0)
    const curConv = Math.min(1, Math.max(0, convRaw <= 1 ? convRaw : convRaw / 100))
    const avg = Number(funnels[zone]?.average_product_value || 0)

    weightedTopWeekEng += p95 * v
    weightedCurrentConv += curConv * v
    totalVisitorWeight += v
    deltaEuro += v * Math.max(0, p95 - curEng) * curConv * avg
  }

  if (!anyZoneHasP95 || totalVisitorWeight === 0) return null

  const topWeeksEngPct = (weightedTopWeekEng / totalVisitorWeight) * 100
  const zoneAvgConvPct = (weightedCurrentConv / totalVisitorWeight) * 100
  const roundedEngPct  = Math.round(topWeeksEngPct * 10) / 10
  const roundedConvPct = Math.round(zoneAvgConvPct * 10) / 10
  const roundedEuro    = Math.round(deltaEuro)

  return {
    topWeeksEngagementPct: roundedEngPct,
    zoneAvgConversionPct:  roundedConvPct,
    deltaEuro: roundedEuro,
    atLevel: roundedEuro <= 0,
  }
}

// Period selector → human label (matches PrioritizationOpportunities)
export function periodLabel(selectedDuration) {
  const labels = {
    1: 'yesterday',
    7: 'the last 7 days',
    30: 'the last 30 days',
    90: 'the last 90 days',
    365: 'the last year',
    1000: 'this week',
    2000: 'this month',
    3000: 'last week',
    4000: 'last month'
  }
  return labels[selectedDuration] ?? `the last ${selectedDuration} days`
}
